import {
  ATOMIC_PENDING_FRESHNESS_TTL_MS,
  getAtomicRequestRuntimeState,
} from './atomic-request-registry.js';
import { serverNow } from './server-clock.js';

export function hasPendingAtomicSnapshots(rows) {
  return Array.isArray(rows) && rows.some((record) => (
    record &&
    typeof record._pendingRequestId === 'string' &&
    record._pendingRequestId.length > 0
  ));
}

export function isActiveAtomicSnapshot(record, now = Date.now()) {
  // runtime clock: _pendingStartedAt is stamped with raw Date.now() at click.
  const requestId = record?._pendingRequestId;
  if (typeof requestId !== 'string' || requestId.length === 0) return false;

  const runtimeState = getAtomicRequestRuntimeState(requestId, now);
  if (runtimeState === 'active') return true;
  if (runtimeState === 'finished') return false;

  const startedAt = record?._pendingStartedAt;
  return Number.isSafeInteger(startedAt) &&
    startedAt > 0 &&
    startedAt <= now &&
    now - startedAt <= ATOMIC_PENDING_FRESHNESS_TTL_MS;
}

export function isStaleAtomicSnapshot(record, now = Date.now()) {
  // runtime clock — same scale as _pendingStartedAt / the atomic registry.
  return Boolean(
    record &&
    typeof record._pendingRequestId === 'string' &&
    record._pendingRequestId.length > 0 &&
    !isActiveAtomicSnapshot(record, now),
  );
}

export function mergeFinancePageRows({
  serverRows,
  localRows,
  localDeleted,
  persistDeleted,
  tombstoned,
  now,
  recordNow = now ?? serverNow(),
  runtimeNow = now ?? Date.now(),
  // 2026-08-31 (P4-GUILLOTINE, тихая потеря правок, решение владельца): было 24h — реалистичный
  // офлайн (выходные, больничный) молча терял правку на следующем page-open refresh. Выровнено
  // на 7d, как MAX_LOCAL_AGE в merge-by-id.js (тот же владелец, то же обоснование: воскрешение
  // удалённого блокируют tombstones на сервере, клиентская гильотина для этого не нужна).
  recentMs = 7 * 24 * 60 * 60 * 1000,
  onDrop,
}) {
  const deleted = localDeleted || new Set();
  const persistedDeletes = persistDeleted || new Set();
  const tombstones = tombstoned || new Set();
  const merged = new Map();

  (serverRows || []).forEach((record) => {
    if (
      record?.id &&
      !deleted.has(record.id) &&
      !persistedDeletes.has(record.id) &&
      !tombstones.has(record.id)
    ) {
      merged.set(record.id, record);
    }
  });

  (localRows || []).forEach((record) => {
    if (
      !record?.id ||
      deleted.has(record.id) ||
      persistedDeletes.has(record.id) ||
      tombstones.has(record.id) ||
      isStaleAtomicSnapshot(record, runtimeNow)
    ) {
      return;
    }
    const serverRecord = merged.get(record.id);
    if (serverRecord) {
      if ((record.updatedAt || 0) > (serverRecord.updatedAt || 0)) {
        merged.set(record.id, record);
      }
      return;
    }
    const age = recordNow - (record.createdAt || record.updatedAt || 0); // record clock
    if (age < recentMs) {
      merged.set(record.id, record);
    } else if (typeof onDrop === 'function') {
      onDrop(record, age);
    }
  });

  return Array.from(merged.values());
}

export function financePageMergeDivergedFromServer(
  result,
  serverRows,
  { now, runtimeNow = now ?? Date.now() } = {},
) {
  const resultById = new Map((result || []).map((record) => [record.id, record]));
  const serverById = new Map((serverRows || []).map((record) => [record.id, record]));

  for (const serverRecord of serverRows || []) {
    const mergedRecord = resultById.get(serverRecord.id);
    if (!mergedRecord) return true;
    if (isActiveAtomicSnapshot(mergedRecord, runtimeNow)) continue;
    if ((mergedRecord.updatedAt || 0) > (serverRecord.updatedAt || 0)) return true;
  }

  for (const mergedRecord of result || []) {
    if (isActiveAtomicSnapshot(mergedRecord, runtimeNow)) continue;
    if (!serverById.has(mergedRecord.id)) return true;
  }
  return false;
}
